import { useState, useEffect } from 'react';
import { debugLog } from '../utils/logger';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/CreateFlow.css';
import { ApiClient, CreationResponse, CreationStatus } from '../api/client';
import { UserInfo } from '../types';
import { CreationType, KovrificationConfig } from '../types/creation';
import { UploadVideoPage } from '../pages/UploadVideoPage';
import { ConfigureCreationPage } from '../pages/ConfigureCreationPage';
import { StrategyType } from '../constants/strategy';
import { CREATION_TYPES } from '../config/creationTypes';
import { BestMomentsConfigRequestData } from '../components/strategies/BestMomentsCreationConfig';

// Default config for Kovrification
const DEFAULT_CONFIG: KovrificationConfig = {
  video_processing: {
    vertical_video_proportion: 0.7,
  },
  caption: {
    enabled: true,
    color: undefined,
    accent_color: undefined,
    font_size: undefined,
    accent_font_size: undefined,
    words_per_line: undefined,
    vertical_margin: 0.7,
  },
};

// Default config for Best Moments
const DEFAULT_BEST_MOMENTS_CONFIG: BestMomentsConfigRequestData = {
  captions_enabled: true,
  caption_config: {
    color: undefined,
    accent_color: undefined,
    font_size: undefined,
    accent_font_size: undefined,
    words_per_line: undefined,
    vertical_margin: 0.7,
    style_id: 'karaoke_default_v1'
  }
};

interface CreateFlowProps {
  onBalanceUpdate: (newBalance: number) => void;
}

export function CreateFlow({ onBalanceUpdate }: CreateFlowProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<'upload' | 'configure' | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [selectedType, setSelectedType] = useState<CreationType | null>(null);
  const [creation, setCreation] = useState<CreationResponse | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [config, setConfig] = useState<KovrificationConfig>({
    ...DEFAULT_CONFIG,
    caption: {
      ...DEFAULT_CONFIG.caption,
      enabled: true,
    }
  });
  const [bestMomentsConfig, setBestMomentsConfig] = useState<BestMomentsConfigRequestData>(DEFAULT_BEST_MOMENTS_CONFIG);

  // Fetch user info when component mounts
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const info = await ApiClient.getUserInfo();
        setUserInfo(info);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    };
    fetchUserInfo();
  }, []);

  // Fetch creation if ID is in URL
  useEffect(() => {
    const creationId = searchParams.get('creationId');
    if (creationId && !creation) {
      const fetchCreation = async () => {
        try {
          const creationData = await ApiClient.getCreation(creationId);
          setCreation(creationData);
        } catch (error) {
          console.error('Failed to fetch creation:', error);
          // If we can't fetch the creation, reset the flow
          setSearchParams({});
          navigate('/');
        }
      };
      fetchCreation();
    }
  }, [searchParams, creation, navigate, setSearchParams]);

  // Initialize the flow based on location state or URL params
  useEffect(() => {
    const initializeFlow = async () => {
      setIsInitializing(true);
      
      // Check URL params first, then location state
      const featureFromParams = searchParams.get('feature');
      const stepFromParams = searchParams.get('step');
      const selectedFeature = featureFromParams || location.state?.selectedFeature;
      
      if (selectedFeature) {
        // Direct creation from feature button or from URL
        const creationType = CREATION_TYPES.find(type => type.id === selectedFeature);
        if (creationType) {
          // Always set the selected type to show the preview
          setSelectedType(creationType);
          
          // Set the step (from URL or default to upload)
          if (stepFromParams === 'configure') {
            setStep('configure');
          } else {
            setStep('upload');
            // Update URL params to reflect current state
            setSearchParams({ feature: selectedFeature, step: 'upload' });
          }
        } else {
          // Invalid feature ID, navigate back
          console.warn('Invalid feature ID passed:', selectedFeature);
          navigate('/');
        }
      } else {
        // No feature selected, navigate back
        console.warn('Navigated to /create without feature selection.');
        navigate('/');
      }
      
      setIsInitializing(false);
    };
    
    initializeFlow();
  }, [location.state, navigate, searchParams, setSearchParams]);

  useEffect(() => {
    if (creation) {
      setConfig(prevConfig => ({
        ...prevConfig,
        caption: {
          ...prevConfig.caption,
          enabled: true,
        }
      }));
    }
  }, [creation]);

  // Handle best moments config change
  const handleBestMomentsConfigChange = (newConfig: BestMomentsConfigRequestData) => {
    setBestMomentsConfig(newConfig);
  };

  // Create a creation based on the selected strategy type
  const createCreation = async (strategyType: StrategyType): Promise<CreationResponse> => {
    switch (strategyType) {
      case StrategyType.KOVRIFICATION:
        return await ApiClient.createKovrification();
      case StrategyType.BEST_MOMENTS:
        return await ApiClient.createBestMoments();
      // Add more cases here as more strategies are added
      default:
        throw new Error(`Unsupported strategy type: ${strategyType}`);
    }
  };

  const handleUploadComplete = async (fileId: string) => {
    if (!selectedType) return;
    
    debugLog("File upload complete in CreateFlowPage, fileId:", fileId);
    
    try {
      // Create the creation based on the selected strategy type
      debugLog("Creating creation with strategy type:", selectedType.strategyType);
      const response = await createCreation(selectedType.strategyType);
      debugLog("Creation created with ID:", response.id);
      
      // Attach the uploaded file to the creation
      debugLog("Attaching file to creation");
      await ApiClient.attachFiles(response.id, [fileId]);
      debugLog("File attached successfully");
      
      // Set the creation state with empty uploaded_files array
      // The actual files will be loaded when we fetch the creation details
      setCreation({
        id: response.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: CreationStatus.DRAFT,
        uploaded_files: [],
        result_files: [],
        payment_id: null,
        pipelines: [],
        strategy: {
          price: selectedType.price,
          name: selectedType.id,
          captions_enabled: true,
          pricingModel: selectedType.pricingModel,
          ...DEFAULT_CONFIG
        }
      });
      
      // Update URL params to include creation ID and step
      const feature = searchParams.get('feature');
      
      // For best moments strategy
      if (selectedType.strategyType === StrategyType.BEST_MOMENTS) {
        // Update URL params (don't move to configure step for best moments)
        setSearchParams({ 
          feature: feature || selectedType.id,
          creationId: response.id 
        });
      } else {
        // For other types, move to configuration step
        setSearchParams({ 
          feature: feature || selectedType.id, 
          step: 'configure',
          creationId: response.id 
        });
        // Move to configuration step
        setStep('configure');
      }
    } catch (error) {
      console.error('Failed to create creation:', error);
      // Handle error - maybe show an error message and go back to selection
      navigate('/');
    }
  };

  const handleBack = () => {
    if (step === 'configure') {
      // Update URL params when going back
      const feature = searchParams.get('feature');
      setSearchParams({ feature: feature || '', step: 'upload' });
      setStep('upload');
    }
  };

  const handleComplete = (newBalance: number) => {
    onBalanceUpdate(newBalance);
  };

  // Show loading state while initializing
  if (isInitializing) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-theme-button border-t-transparent rounded-full animate-spin" />
          <span className="text-theme-hint animate-pulse">Initializing...</span>
        </div>
      </div>
    );
  }

  // Render the appropriate step
  return (
    <div className="min-h-screen bg-theme-bg">
      {step === 'upload' && selectedType && (
        <UploadVideoPage 
          creationTypeName={selectedType.id}
          onUploadComplete={handleUploadComplete}
          isPro={selectedType.proOnly}
          isComingSoon={selectedType.comingSoon}
          onUpgradeClick={() => navigate('/subscription')}
          strategyType={selectedType.strategyType}
          userInfo={userInfo || undefined}
          creationId={creation?.id}
          initialConfig={selectedType.strategyType === StrategyType.BEST_MOMENTS ? bestMomentsConfig : undefined}
          onConfigChange={selectedType.strategyType === StrategyType.BEST_MOMENTS ? handleBestMomentsConfigChange : undefined}
        />
      )}

      {step === 'configure' && selectedType && creation && userInfo && (
        <ConfigureCreationPage
          creationId={creation.id}
          creationTypeName={selectedType.id}
          userInfo={userInfo}
          initialConfig={config}
          creationStrategy={creation.strategy}
          strategyType={selectedType.strategyType}
          onBack={handleBack}
          onComplete={handleComplete}
        />
      )}
    </div>
  );
} 